import React, { useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {ABeagleContext, AccountContext} from '../../contexts/index';
import EXPERIMENTS from '../../constants/ab-tests';
import { useABeagleExperiments } from '@buzzfeed/react-components';
import {
    isOn,
    getExperimentVariant
} from '@buzzfeed/react-components/lib/utils/abeagle';
import { ABEAGLE_HOST } from '../../constants';
import { useUserId } from '../../hooks/useUserId';
import { trackClientExperimentActive } from '../../analytics/client-event-tracking';

function ABeagle({ children }) {
    /**
   * Inside hook, will fetch once userId is a defined value, otherwise will return
   *   default experiments object
   */
    const account = useContext(AccountContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedAccount = useMemo(() => account, [account.id]);
    const userId = useUserId();
    const experiments = useABeagleExperiments({
        abeagleHost: ABEAGLE_HOST,
        data: memoizedAccount,
        experimentConfig: EXPERIMENTS,
        source: 'dotbio',
        userId,
    });

    const loaded = experiments.loaded;
    // quick memoize for eligible experiments
    // @todo - update react-components to return experiment state as memoized object
    const experimentKeys = experiments.eligible
        ? Object.keys(experiments.eligible).join('|')
        : '';

    // fire tracking when experiments or buzz ID has changed
    useEffect(() => {
        if (!loaded || !experimentKeys.length) {
            return;
        }

        const experiment_id = [];

        Object.keys(experiments.eligible).forEach(key => {
            const experiment = experiments.eligible[key];

            // Only send status if bucketed (has variant in 'value')
            if (experiment && experiment.value) {

                experiment_id.push(
                    [
                        key,
                        experiment.id,
                        experiment.version,
                        experiment.value,
                        experiment.variant_id,
                    ].join('|')
                );
            }
        });

        // Fire one Pixiedust event for ALL eligible experiments (only one event should fire)
        trackClientExperimentActive(account, {experiment_id});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded, experimentKeys]);

    /**
   *
   * @type {Function}
   * @param {String} experimentName
   * @param {String} onValue
   */
    const getFeatureFlagValue = (experimentName, onValue = 'on') =>
        isOn(experiments, experimentName, onValue);

    const getExperimentValue = (experimentName, options) =>
        getExperimentVariant(experiments, experimentName, options);

    return (
        <ABeagleContext.Provider
            value={{
                experiments,
                getFeatureFlagValue,
                getExperimentValue,
            }}
        >
            {children}
        </ABeagleContext.Provider>
    );
}

ABeagle.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
};

export default ABeagle;
